import type { ChangeEvent, ReactElement, RefObject } from 'react';
import styled from 'styled-components';
// Shared with the rest of the form; not duplicated so a tweak lands everywhere.
import { FieldLabel } from './ticket-fields';
import type { Attachment } from './use-attachments';

interface Props {
  attachments: Attachment[];
  inputRef: RefObject<HTMLInputElement>;
  onPickFiles: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: (url: string) => void;
}

const AttachBox = styled.div`
  border: 1px dashed rgba(255, 255, 255, 0.16);
  border-radius: 9px;
  padding: 11px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AttachRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const AddImageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  padding: 7px 12px;
  font-weight: 600;
  font-size: 12px;
  color: rgba(213, 221, 238, 0.9);

  i {
    font-size: 14px;
  }
`;

const AttachHint = styled.span`
  font-weight: 500;
  font-size: 10px;
  color: rgba(176, 186, 209, 0.5);
`;

const Previews = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Preview = styled.div`
  position: relative;
  width: 66px;
  height: 66px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: rgba(8, 14, 30, 0.82);
  color: rgb(255, 255, 255);
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0px;

  i {
    font-size: 10px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

/*
 * Previews and the file picker. Drag/drop and paste are NOT handled here — they
 * live on the card so the whole form is a drop target, as the hint says.
 */
export const AttachmentsField = ({
  attachments,
  inputRef,
  onPickFiles,
  onRemove
}: Props): ReactElement => (
  <div>
    <FieldLabel>Attachments</FieldLabel>
    <AttachBox>
      {attachments.length > 0 && (
        <Previews>
          {attachments.map((att) => (
            <Preview key={att.url}>
              <img src={att.url} alt={att.name} />
              <RemoveButton
                type='button'
                title='Remove'
                onClick={(): void => {
                  onRemove(att.url);
                }}
              >
                <i className='far fa-times' />
              </RemoveButton>
            </Preview>
          ))}
        </Previews>
      )}

      <AttachRow>
        <AddImageButton
          type='button'
          onClick={(): void => {
            inputRef.current?.click();
          }}
        >
          <i className='far fa-image' /> Add Image
        </AddImageButton>
        <AttachHint>
          Drag &amp; drop, or paste a screenshot (⌘V) anywhere in this form.
        </AttachHint>
      </AttachRow>

      <HiddenInput ref={inputRef} type='file' accept='image/*' multiple onChange={onPickFiles} />
    </AttachBox>
  </div>
);
